import React, { useState } from "react";
import { CVProfile, CVAnalysisResult } from "./types";
import { cvService } from "../../services/cvService";
import Button from "../UI/Button";
import CVAnalysisModal from "./CVAnalysisModal";

interface CVAnalyzeSectionProps {
  cvData: CVProfile | null;
  onAnalyze?: (data: CVAnalysisResult) => void;
}

const CVAnalyzeSection: React.FC<CVAnalyzeSectionProps> = ({ cvData, onAnalyze }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<CVAnalysisResult | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyzeButton = async () => {
    if (!cvData?.id) {
      setError('CV ID tidak ditemukan');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const result = await cvService.analyzeCV(cvData.id);
      
      if (result.success) {
        setAnalysisResult(result);
        setShowModal(true);
        
        // Call the callback if provided
        if (onAnalyze) {
          onAnalyze(result);
        }
      } else {
        setError(result.error || 'Gagal menganalisis CV');
      }
    } catch (e: any) {
      console.error('Analysis error:', e);
      setError(e.message || 'Terjadi kesalahan saat menganalisis CV');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowAnalysis = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Check if we have analysis results to show different button
  const hasAnalysisResult = analysisResult && analysisResult.success;

  return (
    <>
      {error && (
        <div className="p-4 rounded border bg-red-50 border-red-200 text-red-800 alert-dismissible fade show mb-3" role="alert">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {hasAnalysisResult ? (
        // Show "View Analysis" button if analysis exists
        <Button
          onClick={handleShowAnalysis}
          className="w-full font-bold gradient-accent"
          size="sm"
          primary
        >
          <i className="fas fa-eye mr-1"></i>
          Lihat Detail Analisis
        </Button>
      ) : (
        // Show "Analyze CV" button if no analysis exists
        <Button
          onClick={handleAnalyzeButton}
          className="w-full font-bold gradient-accent"
          size="sm"
          info
          disabled={isLoading || !cvData?.id}
        >
          <i className="fas fa-chart-line mr-1"></i>
          {isLoading ? (
            <>
              <div
                className="animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 w-4 h-4 mr-2"
                role="status"
              >
                <span className="invisible">Memuat...</span>
              </div>
              Menganalisis...
            </>
          ) : (
            <>
              Analisis CV Anda
            </>
          )}
        </Button>
      )}

      {/* Analysis Results Modal */}
      <CVAnalysisModal
        show={showModal}
        onHide={handleCloseModal}
        analysisData={analysisResult}
      />
    </>
  );
};

export default CVAnalyzeSection;